import { mapState } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import pullAt from 'lodash/pullAt';
import get from 'lodash/get';
import ApiService from '@/services/apiService';
import viewMixin from './viewMixin';
export default {
  name: 'CoreConsumersChainsEdit',
  mixins: [viewMixin],
  props: {
    accounts: {
      type: Array,
      required: true,
      default: () => []
    },
    selectedAccount: {
      type: Object,
      required: false,
      default: () => ({})
    },
    consumerModel: {
      type: Object,
      required: true,
      default: () => ({})
    },
    triggerSources: {
      type: Array,
      required: true,
      default: () => []
    },
    bridgeTypes: {
      type: Array,
      required: true,
      default: () => []
    },
    isCreateDialog: {
      type: Boolean,
      required: true,
      default: true
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      newChainItemType: null,
      formValid: false,
      bridges: [],
      account: this.selectedAccount,
      filters: [],
      transformers: [],
      textboxFields: ['payload', 'html', 'text', 'frm_payload']
    };
  },
  computed: {
    ...mapState('app', ['color', 'inpOutlined', 'inpFilled', 'permissions']),
    requiredRule() {
      return [v => !!v || this.$i18n_t('Common.fieldRequired')];
    },
    chainBridgeItem() {
      const chain = this.consumerModel.chain || [];
      return this.chainHasOneBridge && chain[chain.length - 1].action === 'ds-bridge' ? chain[chain.length - 1] : {};
    },
    chainHasOneBridge() {
      return (this.consumerModel.chain || []).filter(c => c.action === 'ds-bridge').length === 1;
    },
    chainsLastItemIsBridge() {
      return this.chainBridgeItem.action === 'ds-bridge';
    },
    chainErrorMessage() {
      return !this.chainHasOneBridge ? this.$i18n_t('Bridges.Consumers.chains.errors.mustHaveOneConnector') : true;
    },
    chainBridgeRules() {
      return [this.requiredRule[0], () => this.chainsLastItemIsBridge || this.$i18n_t('Bridges.Consumers.chains.errors.connectorLastStep')];
    },
    chainItemTypes() {
      const filterParam = this.filters.length ? {
        value: 'filter',
        text: this.$i18n_t('Bridges.Consumers.chains.filter')
      } : null;
      const transformerParam = this.transformers.length ? {
        value: 'transform',
        text: this.$i18n_t('Bridges.Consumers.chains.transformer')
      } : null;
      const bridgeParam = this.bridges.length && !this.chainHasOneBridge ? {
        value: 'bridge',
        text: this.$i18n_t('accMgmt.tenant.bridges.title')
      } : null;
      return [filterParam, transformerParam, bridgeParam].filter(v => v);
    },
    chainItemsTableHeader() {
      return [{
        value: 'order',
        text: this.$i18n_t('NiotaClassic.DeviceTypes.form.parser.order'),
        sortable: false
      }, {
        text: this.$i18n_t('Bridges.Consumers.chains.item'),
        sortable: false
      }];
    },
    getColumnWidth() {
      return (param, paramConfig) => {
        if (paramConfig.width && ['m', 'l'].indexOf(paramConfig.width) >= 0) {
          return param.width === 'm' ? 6 : 12;
        }
        return this.textboxFields.includes(param) ? 12 : 4;
      };
    }
  },
  watch: {
    formValid() {
      this.$emit('change-valid-form', this.formValid);
    },
    account(account) {
      this.setBridgeConfigs(account);
      this.fetchTransformers([account], {
        'loading-flag': false
      });
      this.fetchFilters([account], {
        'loading-flag': false
      });
      this.consumerModel.chain = [];
      this.newChainItemType = null;
    }
  },
  created() {
    if (!this.selectedAccount) {
      return;
    }
    this.setBridgeConfigs(this.selectedAccount);
    this.fetchTransformers([this.selectedAccount], {
      'loading-flag': false
    });
    this.fetchFilters([this.selectedAccount], {
      'loading-flag': false
    });
  },
  methods: {
    /** these rules make sure that the content-types of filter/transformer steps are compatible
     *  e.g. if a transformer outputs XML and a filter requires JSON, then the UI shall complain about it.
     */
    transformerOrFilterRule(index) {
      if (index === 0) {
        // the first step obviously has no conditions yet
        return this.requiredRule;
      }
      return [this.requiredRule[0], () => {
        const currentStep = get(this.consumerModel.chain, `[${index}]`);
        const currId = get(currentStep, 'parameter.id');
        const priorStep = get(this.consumerModel.chain, `[${index - 1}]`);
        const priorId = get(priorStep, 'parameter.id');
        if (!currId || !priorId) {
          return true;
        }
        let currentInput = null;
        /* eslint-disable prettier/prettier */
        if (currentStep.action === 'ds-transform') {
          currentInput = get(this.transformers.find(t => t.id === currId), 'transformer.contentTypes.input');
        } else if (currentStep.action === 'ds-filter') {
          currentInput = get(this.filters.find(f => f.id === priorId), 'filter.inputType');
        }

        // only a transformer as an _output_ type, otherwise it is a filter that does not transform its data
        // so in case the prior step is a filter, just put the current input-type,
        // so the comparision underneath works out fine
        const priorOutput = priorStep.action === 'ds-transform' ? get(this.transformers.find(t => t.id === priorId), 'transformer.contentTypes.output') : currentInput;
        /* eslint-enable prettier/prettier */

        return [currentInput, priorOutput].indexOf('generic') >= 0 || priorOutput === currentInput || this.$i18n_t('Bridges.Consumers.chains.errors.incompatibleSteps');
      }];
    },
    parametersForBridgeIdx(bridgeIdx) {
      const method = this.methodForBridgeIdx(bridgeIdx);
      const params = get(this.methodForBridgeIdx(bridgeIdx), 'methodDef.parameters') || {};
      const bridgeType = method.bridgeType;
      delete params[get(bridgeType, 'manifest.functionScope.extension.consumer.publish.payload')];
      return params;
    },
    methodForBridgeIdx(bridgeIdx) {
      const bridge = this.bridges.find(b => b.id === get(this.chainBridgeItem, `parameter.configs[${bridgeIdx}].id`));
      if (!bridge) {
        return {};
      }
      const bridgeType = this.bridgeTypes.find(b => b.type === bridge.type);
      const methodName = get(bridgeType, 'manifest.functionScope.extension.consumer.publish.method');
      const methodDef = get(bridgeType, `manifest.commands.instance.${methodName}`);
      return {
        bridge,
        bridgeType,
        methodName,
        methodDef
      };
    },
    payloadDescriptionForBridgeIdx(bridgeIdx) {
      const bridge = this.bridges.find(b => b.id === get(this.chainBridgeItem, `parameter.configs[${bridgeIdx}].id`));
      if (!bridge) {
        return null;
      }
      const payloadKey = `Bridges.Consumers.connectorTypes.${bridge.type.replace('bridge-', '')}.payloadDescription`;
      return this.$i18n_te(payloadKey) ? this.$i18n_t(payloadKey) : null;
    },
    contentTypeForBridgeIdx(bridgeIdx, useFallback = true) {
      const {
        bridgeType,
        methodDef
      } = this.methodForBridgeIdx(bridgeIdx);
      let outputContentType = undefined;
      const encodingParam = get(bridgeType, 'manifest.functionScope.extension.consumer.publish.contentType.parameter');
      const encodingDef = get(methodDef, `parameters.${encodingParam}`);
      if (encodingDef) {
        const chainSteps = this.consumerModel.chain.map(c => {
          switch (c.action) {
            case 'ds-transform':
              return this.transformers.find(t => t.id === c.parameter.id);
            case 'ds-filter':
              return this.filters.find(f => f.id === c.parameter.id);
            default:
              return null;
          }
        });

        // get the latest output type; which does not necessarily have to come from the prior step
        for (let j = this.consumerModel.chain.length - 1; j >= 0; j--) {
          if (this.consumerModel.chain[j].action === 'ds-transform' && chainSteps[j] !== null && chainSteps[j] !== undefined) {
            outputContentType = chainSteps[j].transformer.contentTypes.output;
            break;
          }
          if (this.consumerModel.chain[j].action === 'ds-filter' && chainSteps[j] !== null && chainSteps[j] !== undefined) {
            // otherwise use the INPUT type of filters
            outputContentType = chainSteps[j].filter.inputType;
            break;
          }
        }
        const typeMapping = get(bridgeType, 'manifest.functionScope.extension.consumer.publish.contentType.mapping');
        outputContentType = typeMapping[outputContentType] || (useFallback ? outputContentType : undefined);
      }
      return {
        outputContentType,
        encodingParam
      };
    },
    rulesForBridgeIdx(bridgeIndex) {
      return [...(bridgeIndex === 0 ? this.chainBridgeRules : this.requiredRule), () => {
        const contentType = this.contentTypeForBridgeIdx(bridgeIndex);
        return (
          // if the output-type could not be determined (e.g. there is not even a prior step), it is valid
          contentType.outputContentType === undefined ||
          // or it is a "wildcard" content, i.e. anything matches
          contentType.outputContentType === 'generic' ||
          // otherwise the output-type must match
          contentType.outputContentType === get(this.chainBridgeItem, `parameter.configs[${bridgeIndex}].params.${contentType.encodingParam}`) || this.$i18n_t('Bridges.Consumers.chains.errors.incompatibleSteps')
        );
      }];
    },
    pickedBridge(bridgeIndex) {
      // if a bridge is selected, pre-select the desired output-type according to the current chain, if possible
      // add the `noFallback` flag here, to make sure if no mapping is possible, the outputContentType is undefined,
      // i.e. effectly it is still not set afterward
      const contentType = this.contentTypeForBridgeIdx(bridgeIndex, false);
      if (get(this.chainBridgeItem, 'parameter.configs') && !this.chainBridgeItem.parameter.configs[bridgeIndex][contentType.encodingParam]) {
        this.chainBridgeItem.parameter.configs[bridgeIndex][contentType.encodingParam] = contentType.outputContentType;
      }
    },
    addNewChainItem() {
      const newItem = {
        action: `ds-${this.newChainItemType}`,
        parameter: {}
      };
      switch (this.newChainItemType) {
        case 'bridge':
          newItem.parameter.configs = [];
          break;
      }
      this.consumerModel.chain.push(newItem);
      switch (this.newChainItemType) {
        case 'bridge':
          // needs to be called AFTER being pushed to consumerModel
          // is a lil ugly with 2 switch right after another, but seems to be the only way
          this.addBridgeConfig(this.consumerModel.chain.length - 1);
          break;
      }
      this.$nextTick(() => {
        this.$refs.form.validate();
      });
    },
    deleteChainItem(index) {
      pullAt(this.consumerModel.chain, [index]);
      this.consumerModel.chain = cloneDeep(this.consumerModel.chain);
      this.$nextTick(() => {
        this.$refs.form.validate();
      });
    },
    changeChainItemOrder(currentIndex, direction) {
      let newIndex = Math.min(this.consumerModel.chain.length - 1, Math.max(0, currentIndex + (direction === 'up' ? -1 : 1)));
      if (newIndex !== currentIndex) {
        const chain = cloneDeep(this.consumerModel.chain);
        const otherObject = chain[newIndex];
        chain[newIndex] = chain[currentIndex];
        chain[currentIndex] = otherObject;
        this.consumerModel.chain = chain;
        this.$nextTick(() => {
          this.$refs.form.validate();
        });
      }
    },
    addBridgeConfig(chainIdx) {
      this.consumerModel.chain[chainIdx].parameter.configs.push({
        params: {}
      });
      this.$nextTick(() => {
        this.$refs.form.validate();
      });
    },
    deleteBridgeConfig(chainIdx, index) {
      pullAt(this.consumerModel.chain[chainIdx].parameter.configs, [index]);
      this.consumerModel.chain = cloneDeep(this.consumerModel.chain);
      this.$nextTick(() => {
        this.$refs.form.validate();
      });
    },
    setBridgeConfigs(account) {
      if (!account) {
        return;
      }
      ApiService.getBridgesForTenant(account.id).then(data => {
        data = data.length ? data.map(b => {
          const bridgeType = this.bridgeTypes.find(t => t.type === b.type);
          b.displayName = bridgeType ? get(bridgeType, 'manifest.displayName') : '';
          return b;
        }) : [];
        this.bridges = data.filter(b => {
          const type = this.bridgeTypes.find(t => t.type === b.type);
          return type && get(type, 'manifest.functionScope.extension.consumer');
        });
      }).catch(error => {
        console.error(error);
      });
    }
  }
};